import { Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ShieldOff, TriangleAlert } from 'lucide-react';

import FullPageFrame from '../components/shared/FullPageFrame';
import RoleBuilderView from '../components/role-builder/RoleBuilderView';
import RoleBuilderProvider from '../context/RoleBuilderProvider';
import { PERMISSIONS } from '../constants/permissions';
import { usePermission } from '../hooks/usePermission';
import { useRoles } from '../hooks/useRoles';
import { useStudiosCatalog } from '../hooks/useStudiosCatalog';

/** Loads the roles (Start from, duplicate names) and the studios catalog, then mounts the wizard. */
function RoleNewLoader({ basePath }) {
  const { t } = useTranslation('people-access');
  const { roles, isLoading: rolesLoading, error: rolesError } = useRoles();
  const { studios, isLoading: studiosLoading, error: studiosError } = useStudiosCatalog();

  if (rolesLoading || studiosLoading) {
    return (
      <FullPageFrame>
        <Skeleton height="360px" borderRadius="12px" />
      </FullPageFrame>
    );
  }
  if (rolesError || studiosError) {
    return (
      <FullPageFrame>
        <StatusMessage icon={TriangleAlert} title={t('roles.builder.loadError')} subtitle={t('roles.builder.loadErrorSubtitle')} />
      </FullPageFrame>
    );
  }

  return (
    <RoleBuilderProvider studios={studios} roles={roles}>
      <RoleBuilderView basePath={basePath} />
    </RoleBuilderProvider>
  );
}

/**
 * New role (`roles/new`): the 3-step builder. Gated by the roles-manage permission; the data is
 * only requested once the user is allowed in.
 *
 * @param {object} props
 * @param {string} props.basePath Where the host mounted People & Access.
 */
function RoleNewPage({ basePath }) {
  const { t } = useTranslation('people-access');
  const { allowed, loading } = usePermission(PERMISSIONS.ROLES_MANAGE);

  if (loading) {
    return (
      <FullPageFrame>
        <Skeleton height="360px" borderRadius="12px" />
      </FullPageFrame>
    );
  }
  if (!allowed) {
    return (
      <FullPageFrame>
        <StatusMessage icon={ShieldOff} title={t('roles.builder.forbidden.title')} subtitle={t('roles.builder.forbidden.subtitle')} />
      </FullPageFrame>
    );
  }

  return <RoleNewLoader basePath={basePath} />;
}

export default RoleNewPage;
